// Gera os snippets de request a partir do próprio endpoint.
//
// Escrever 4 linguagens × ~100 endpoints à mão é inviável de manter: o dia em
// que um parâmetro muda, três dos quatro exemplos ficam desatualizados sem
// ninguém perceber. Aqui eles derivam de method/path/body, então nunca divergem.
// Quando o spec traz `x-codeSamples`, o exemplo escrito à mão ganha — é para
// isso que a extensão existe.

import { type Operation, type Param } from "./types.ts";

const DEFAULT_LANGS = ["cURL", "JavaScript", "Python", "PHP"];

// Query string de exemplo, só com os parâmetros obrigatórios — um curl com 12
// parâmetros opcionais não é copiável.
function exampleQuery(params: Param[] = []): string {
  const required = params.filter((p) => p.loc === "query" && p.required);
  if (!required.length) return "";
  const pairs = required.map((p) => `${encodeURIComponent(p.name)}=${encodeURIComponent(String(p.example ?? ""))}`);
  return `?${pairs.join("&")}`;
}

// Path params viram valores de exemplo: /booking/{id} → /booking/778213
function fillPath(path: string, params: Param[] = []): string {
  return path.replace(/\{([^}]+)\}/g, (match, name: string) => {
    const p = params.find((x) => x.loc === "path" && x.name === name);
    const value = p?.example;
    return value === undefined || value === null || value === "" ? match : String(value);
  });
}

export function buildSnippets(
  op: Operation,
  { baseVar = "$BASE", tokenVar = "$TOKEN" }: { baseVar?: string; tokenVar?: string } = {}
): { snippets: Record<string, string>; langs: string[] } {
  const url = `${baseVar}${fillPath(op.path, op.params)}${exampleQuery(op.params)}`;
  const bodyRaw = op.body?.example || null;
  const bodyInline = bodyRaw ? bodyRaw.replace(/\s+/g, " ").trim() : null;
  const isAuth = /authenticate|\/login/i.test(op.path);
  const authHeader = isAuth ? null : `Authorization: Bearer ${tokenVar}`;

  const curlLines = [`curl -X ${op.method} "${url}"`];
  if (authHeader) curlLines.push(`  -H "${authHeader}"`);
  if (bodyRaw) {
    curlLines.push(`  -H "Content-Type: application/json"`);
    curlLines.push(`  -d '${bodyInline}'`);
  }
  const cURL = curlLines.join(" \\\n");

  const jsHeaders = [
    ...(authHeader ? [`    "Authorization": "Bearer ${tokenVar}",`] : []),
    ...(bodyRaw ? [`    "Content-Type": "application/json",`] : []),
  ].join("\n");

  const JavaScript = `const res = await fetch("${url}", {
  method: "${op.method}",
  headers: {
${jsHeaders || '    "Accept": "application/json",'}
  },${bodyRaw ? `\n  body: JSON.stringify(${bodyInline}),` : ""}
});
const data = await res.json();`;

  const pyHeaders = [
    ...(authHeader ? [`        "Authorization": "Bearer ${tokenVar}",`] : []),
    ...(bodyRaw ? [`        "Content-Type": "application/json",`] : []),
  ].join("\n");

  const Python = `import requests

res = requests.request(
    "${op.method}", "${url}",
    headers={
${pyHeaders || '        "Accept": "application/json",'}
    },${bodyRaw ? `\n    json=${bodyInline},` : ""}
)
data = res.json()`;

  const phpHeaders = [
    ...(authHeader ? [`    "${authHeader}",`] : []),
    ...(bodyRaw ? [`    "Content-Type: application/json",`] : []),
  ].join("\n");

  const PHP = `$ch = curl_init("${url}");
curl_setopt_array($ch, [
  CURLOPT_CUSTOMREQUEST => "${op.method}",
  CURLOPT_HTTPHEADER => [
${phpHeaders || '    "Accept: application/json",'}
  ],${bodyRaw ? `\n  CURLOPT_POSTFIELDS => json_encode(${bodyInline}),` : ""}
  CURLOPT_RETURNTRANSFER => true,
]);
$data = json_decode(curl_exec($ch), true);`;

  const generated = { cURL, JavaScript, Python, PHP };

  // x-codeSamples sobrescreve o gerado e pode adicionar linguagens novas.
  const custom: Record<string, string> = {};
  for (const sample of op.codeSamples || []) {
    const label = sample.label || sample.lang;
    if (label && sample.source) custom[label] = sample.source;
  }

  const merged: Record<string, string> = { ...generated, ...custom };
  const langs = [
    ...DEFAULT_LANGS.filter((l) => merged[l]),
    ...Object.keys(custom).filter((l) => !DEFAULT_LANGS.includes(l)),
  ];

  return { snippets: merged, langs };
}

export default buildSnippets;
